import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Chart,
  ChartConfiguration,
  ChartData,
  ChartOptions,
  ChartType,
  Plugin,
  registerables,
} from 'chart.js';

// Register built-in charts/controllers/elements once.
let chartRegistered = false;
function ensureChartRegistered() {
  if (chartRegistered) return;
  Chart.register(...registerables);
  chartRegistered = true;
}

@Component({
  selector: 'app-chartjs',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './chartjs.component.html',
  styleUrls: ['./chartjs.component.scss'],
})
export class ChartjsComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() type: ChartType = 'bar';
  @Input() data: ChartData = { datasets: [] };
  @Input() options: ChartOptions = {};
  @Input() plugins: Plugin[] = [];
  @Input() heightPx: number = 220;

  @ViewChild('canvas', { static: true }) private canvasRef!: ElementRef<HTMLCanvasElement>;
  private chart: Chart | null = null;
  private viewReady = false;
  private lastType: ChartType = 'bar';

  ngAfterViewInit(): void {
    this.viewReady = true;
    ensureChartRegistered();
    this.lastType = this.type;
    this.renderChart();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    if (!this.viewReady) return;
    // Update in-place if possible to avoid re-creating canvas + losing transitions.
    if (this.chart) {
      // Chart.js typings model config as a union; changing `type` in-place can be awkward.
      // Re-create the chart if the type changes.
      if (this.type !== this.lastType) {
        this.lastType = this.type;
        this.renderChart();
        return;
      }
      this.chart.data = this.data as any;
      this.chart.options = this.options as any;
      this.chart.update();
      return;
    }
    this.renderChart();
  }

  private renderChart(): void {
    if (!this.canvasRef?.nativeElement) return;
    if (this.chart) {
      try {
        this.chart.destroy();
      } catch {}
      this.chart = null;
    }
    const ctx = this.canvasRef.nativeElement.getContext('2d');
    if (!ctx) return;

    const config: ChartConfiguration = {
      type: this.type,
      data: this.data,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: 'bottom' },
          tooltip: { enabled: true },
        },
        ...(this.options ?? {}),
      } as any,
      plugins: this.plugins ?? [],
    };

    this.chart = new Chart(ctx, config);
  }

  ngOnDestroy(): void {
    if (!this.chart) return;
    try {
      this.chart.destroy();
    } catch {}
    this.chart = null;
  }
}

